import validate from "validate.js";
import {
  addMinutes,
  differenceInDays,
  getUnixTime,
  isAfter,
  isBefore,
  isValid,
} from "date-fns";
import { validate as validateUuid } from "uuid";
import { checkMimeCompatibility } from "../../helper/files";

(validate as any).options = { fullMessages: false, format: "custom" };

validate.formatters.custom = function (errors) {
  return errors.reduce(
    (acc, e) => ({
      ...acc,
      [e.attribute]: [
        ...(acc[e.attribute] || []),
        {
          content: { local: e.error },
          validator: e.validator,
          options: e.options,
        },
      ],
    }),
    {},
  );
};

validate.validators.presence.options = {
  message: "Validation::Presence",
};

validate.validators.format.options = {
  message: "Validation::Format",
};

validate.validators.equality.options = {
  message: "Validation::Equality",
};

validate.validators.length.options = {
  tooShort: "Validation::TooShort",
};

validate.validators.datetime.options = {
  notValid: "Validation::NotValid",
  tooEarly: "Validation::TooEarly",
  tooLate: "Validation::TooLate",
};

validate.validators.numericality.options = {
  notValid: "Validation::NotNumber",
  notInteger: "Validation::NotInteger",
  notGreaterThanOrEqualTo: "Validation::NotGreaterThanOrEqualTo",
  notLessThanOrEqualTo: "Validation::NotLessThanOrEqualTo",
};

validate.validators.format = (value, options) => {
  if (!value) return [];
  const { pattern } = options;
  const regexp = new RegExp(pattern, "g");
  return regexp.test(value) ? [] : ["Validation::Format"];
};

validate.validators.pattern = () => {
  // Handled by input field
  return [];
};

validate.validators.accept = () => {
  // Handled by input field
  return [];
};

validate.validators.mimeType = (value, options) => {
  if (!value || options === null) return [];
  if (Array.isArray(options)) {
    if (options.length === 0) return [];
    if (options.indexOf(value.type) === -1) return ["Validation::MimeType"];
    return [];
  }
  return !checkMimeCompatibility(options, value.type)
    ? ["Validation::MimeType"]
    : [];
};

validate.validators.mimeType.options = {
  message: "Validation::MimeType",
};

validate.validators.terms = (value) => {
  if (value === true) return [];
  return ["Validation::Terms"];
};

validate.validators.pdf = (value, options) => {
  if (!value) return undefined;

  const { printRequired, downloadRequired } = options || {};

  const errors = [
    ...(printRequired && !value.printed ? ["Validation::NotPrinted"] : []),
    ...(downloadRequired && !value.downloaded
      ? ["Validation::NotDownloaded"]
      : []),
  ];

  return errors.length > 0 ? errors : undefined;
};

validate.validators.uuid = (value) => {
  return validateUuid(value) ? null : "Validation::Uuid";
};

validate.validators.json = (value) => {
  if (!value) return undefined;
  try {
    JSON.parse(value);
    return undefined;
  } catch {
    return "Validation::JSON";
  }
};

validate.validators.daterange = (value, options) => {
  if (!value) return undefined;
  if (!value[0] || !value[1]) return "Validation::Presence";

  const {
    beginEarliest,
    beginLatest,
    endEarliest,
    endLatest,
    minLength,
    maxLength,
  } = options;
  const length = differenceInDays(value[1], value[0]);

  const errors = [
    ...(!isValid(value[0]) || !isValid(value[1])
      ? ["Validation::NotValid"]
      : []),
    ...(maxLength && length > maxLength ? ["Validation::RangeTooLong"] : []),
    ...(minLength && length < minLength ? ["Validation::RangeTooShort"] : []),
    ...(!beginEarliest || isAfter(value[0], addMinutes(beginEarliest, -1))
      ? []
      : ["Validation::BeginTooEarly"]),
    ...(!beginLatest || isBefore(value[0], addMinutes(beginLatest, 1))
      ? []
      : ["Validation::BeginTooLate"]),
    ...(!endEarliest || isAfter(value[1], addMinutes(endEarliest, -1))
      ? []
      : ["Validation::EndTooEarly"]),
    ...(!endLatest || isBefore(value[1], addMinutes(endLatest, 1))
      ? []
      : ["Validation::EndTooLate"]),
  ];

  return errors.length > 0 ? errors : undefined;
};

validate.extend(validate.validators.datetime, {
  parse: (value) => getUnixTime(value),
  format: () => "",
});

export default validate;
